import { CrashMonitor } from '../monitor/crashMonitor';
import { logger as moduleLogger } from '@/crashReports/utils/logger';
const logger = moduleLogger.createSubLogger('CrashMonitor');
/**
 * 崩溃模拟器
 * 提供多种方式来模拟前端崩溃场景
 */
export class CrashSimulator {
  private isRunning = false;

  /**
   * 方法1: 模拟内存溢出 (相对安全)
   * 通过创建大量对象来消耗内存
   */
  async simulateMemoryLeak() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟内存泄漏...');
    this.isRunning = true;
    const memoryLeakArray: any[] = [];
    let iteration = 0;

    const leakMemory = () => {
      if (!this.isRunning) return;

      try {
        // 创建大量对象
        for (let i = 0; i < 10000; i++) {
          memoryLeakArray.push({
            data: new Array(1000).fill(Math.random()),
            timestamp: Date.now(),
            iteration: iteration++,
          });
        }

        logger.log(`💾 已分配内存: ${memoryLeakArray.length} 个对象`);

        // 继续泄漏内存
        setTimeout(leakMemory, 100);
      } catch (error) {
        logger.log('💥 内存分配失败，可能接近OOM:', error);
        this.stopSimulation();
      }
    };

    leakMemory();
  }

  /**
   * 方法2: 模拟无限循环导致的主线程阻塞
   */
  async simulateInfiniteLoop() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟无限循环...');
    this.isRunning = true;
    // 等待2秒后开始无限循环
    setTimeout(() => {
      logger.log('🔄 开始无限循环，主线程将被阻塞...');

      // 无限循环会阻塞主线程，导致心跳停止
      while (true) {
        // 这个循环会一直执行，阻塞所有其他JavaScript代码
        const result = Math.random() * Math.random();
        if (result > 0.999999) {
          // 理论上永远不会到达这里
          break;
        }
      }
    }, 2000);
  }

  /**
   * 方法3: 模拟JavaScript错误导致的心跳停止
   */
  async simulateJavaScriptError() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('🧪 开始模拟JavaScript错误...');
    this.isRunning = true;

    // 等待2秒后抛出错误
    setTimeout(() => {
      logger.log('💥 即将抛出JavaScript错误...');

      // 抛出错误会中断JavaScript执行
      throw new Error('模拟的JavaScript错误');
    }, 2000);
  }

  /**
   * 方法4: 模拟页面冻结 (通过大量计算)
   */
  async simulatePageFreeze() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟页面冻结...');
    this.isRunning = true;
    // 等待2秒后开始大量计算
    setTimeout(() => {
      logger.log('❄️ 开始大量计算，页面可能冻结...');

      // 执行大量计算，可能导致页面冻结
      let result = 0;
      for (let i = 0; i < 1000000000; i++) {
        result += Math.sqrt(i);
        if (i % 1000000 === 0) {
          logger.log(`计算进度: ${i / 10000000}%`);
        }
      }

      logger.log('计算完成:', result);
      this.stopSimulation();
    }, 2000);
  }

  /**
   * 方法5: 模拟DOM操作阻塞
   */
  async simulateDOMBlock() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟DOM操作阻塞...');
    this.isRunning = true;
    // 创建大量DOM元素
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    document.body.appendChild(container);

    const blockDOM = () => {
      if (!this.isRunning) return;

      try {
        for (let i = 0; i < 1000; i++) {
          const element = document.createElement('div');
          element.innerHTML = new Array(100).fill('test').join('');
          container.appendChild(element);
        }

        logger.log(`🏗️ 已创建 ${container.children.length} 个DOM元素`);

        // 继续创建
        setTimeout(blockDOM, 50);
      } catch (error) {
        logger.log('💥 DOM操作失败:', error);
        this.stopSimulation();
      }
    };

    blockDOM();
  }

  /**
   * 方法6: 模拟定时器阻塞
   */
  async simulateTimerBlock() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('🧪 开始模拟定时器阻塞...');
    this.isRunning = true;

    // 创建大量定时器
    const timers: number[] = [];

    for (let i = 0; i < 10000; i++) {
      const timer = setInterval(() => {
        // 执行一些计算密集型操作
        let result = 0;
        for (let j = 0; j < 1000; j++) {
          result += Math.random();
        }
      }, 1);

      // @ts-ignore
      timers.push(timer);
    }

    logger.log('⏰ 已创建大量定时器，可能导致性能问题...');

    // 5秒后清理定时器
    setTimeout(() => {
      timers.forEach((timer) => clearInterval(timer));
      logger.log('🧹 已清理所有定时器');
      this.stopSimulation();
    }, 5000);
  }

  /**
   * 方法7: 模拟网络请求阻塞
   */
  async simulateNetworkBlock() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟网络阻塞...');
    this.isRunning = true;

    // 创建大量未完成的网络请求
    const requests: Promise<any>[] = [];

    for (let i = 0; i < 100; i++) {
      const request = fetch('/api/test', {
        method: 'POST',
        body: JSON.stringify({ data: new Array(10000).fill(Math.random()) }),
      }).catch(() => {
        // 忽略错误
      });

      requests.push(request);
    }

    logger.log('🌐 已发起大量网络请求，可能导致阻塞...');

    // 10秒后清理
    setTimeout(() => {
      logger.log('🧹 网络阻塞测试完成');
      this.stopSimulation();
    }, 10000);
  }

  /**
   * 方法8: 最简单的方法 - 直接停止心跳
   */
  async simulateHeartbeatStop(crashMonitor: CrashMonitor) {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟心跳停止...');
    this.isRunning = true;

    // 等待2秒后停止心跳
    setTimeout(() => {
      logger.log('💔 停止心跳，模拟崩溃...');
      crashMonitor.stop();
      this.stopSimulation();
    }, 2000);
  }

  /**
   * 方法9: 模拟WebWorker崩溃
   */
  async simulateWorkerCrash() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟WebWorker崩溃...');
    this.isRunning = true;
    // 创建一个会崩溃的Worker
    const crashWorker = new Worker(
      `
      // 在Worker中执行会导致崩溃的代码
      while (true) {
        // 无限循环
      }
    `,
      { type: 'module' }
    );

    // 等待一段时间后终止Worker
    setTimeout(() => {
      crashWorker.terminate();
      logger.log('💥 WebWorker已终止');
      this.stopSimulation();
    }, 3000);
  }

  /**
   * 方法10: 模拟浏览器标签页崩溃 (通过iframe)
   */
  async simulateTabCrash() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('🧪 开始模拟标签页崩溃...');
    this.isRunning = true;

    // 创建一个iframe并加载会导致崩溃的内容
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    // 在iframe中执行可能导致崩溃的代码
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(`
        <script>
          // 在iframe中执行无限循环
          while (true) {
            // 这可能导致iframe崩溃
          }
        </script>
      `);
      iframeDoc.close();
    }

    logger.log('📄 已创建可能导致崩溃的iframe...');

    // 5秒后清理
    setTimeout(() => {
      document.body.removeChild(iframe);
      logger.log('已清理iframe');
      this.stopSimulation();
    }, 5000);
  }

  /**
   * 方法11: 模拟页面可见性变化
   */
  async simulateVisibilityChange() {
    if (this.isRunning) {
      logger.warn('⚠️ 已有模拟测试在运行中');
      return;
    }

    logger.log('开始模拟页面可见性变化...');
    this.isRunning = true;

    // 模拟页面隐藏
    Object.defineProperty(document, 'hidden', {
      writable: true,
      value: true,
    });
    document.dispatchEvent(new Event('visibilitychange'));

    logger.log('📱 页面已隐藏');

    // 2秒后恢复显示
    setTimeout(() => {
      Object.defineProperty(document, 'hidden', {
        writable: true,
        value: false,
      });
      document.dispatchEvent(new Event('visibilitychange'));

      logger.log('📱 页面已显示');

      // 2秒后停止测试
      setTimeout(() => {
        this.stopSimulation();
      }, 2000);
    }, 2000);
  }

  /**
   * 运行所有模拟测试
   */
  async runAllSimulations(crashMonitor?: CrashMonitor) {
    logger.log('开始运行所有崩溃模拟测试\n');

    const simulations = [
      { name: 'JavaScript错误', method: () => this.simulateJavaScriptError() },
      { name: '定时器阻塞', method: () => this.simulateTimerBlock() },
      { name: 'DOM操作阻塞', method: () => this.simulateDOMBlock() },
      { name: '页面可见性变化', method: () => this.simulateVisibilityChange() },
      { name: '网络阻塞', method: () => this.simulateNetworkBlock() },
      { name: '内存泄漏', method: () => this.simulateMemoryLeak() },
      { name: '页面冻结', method: () => this.simulatePageFreeze() },
      { name: 'WebWorker崩溃', method: () => this.simulateWorkerCrash() },
      { name: '标签页崩溃', method: () => this.simulateTabCrash() },
      { name: '无限循环', method: () => this.simulateInfiniteLoop() },
    ];
    if (crashMonitor) {
      simulations.push({
        name: '心跳停止',
        method: () => this.simulateHeartbeatStop(crashMonitor),
      });
    }

    for (const simulation of simulations) {
      try {
        logger.log(`\n🧪 测试: ${simulation.name}`);
        await simulation.method();

        // 等待一段时间让测试完成
        await this.sleep(3000);

        this.isRunning = false;
      } catch (error) {
        logger.error(`❌ ${simulation.name} 测试失败:`, error);
        this.isRunning = false;
      }
    }

    logger.log('\n所有模拟测试完成！');
  }

  /**
   * 运行安全测试 (推荐用于生产环境)
   */
  async runSafeTests(crashMonitor?: CrashMonitor) {
    logger.log('🛡️ 开始运行安全测试 (推荐用于生产环境)\n');

    const safeSimulations = [
      { name: 'JavaScript错误', method: () => this.simulateJavaScriptError() },
      { name: '页面可见性变化', method: () => this.simulateVisibilityChange() },
    ];
    if (crashMonitor) {
      safeSimulations.push({
        name: '心跳停止',
        method: () => this.simulateHeartbeatStop(crashMonitor),
      });
    }

    for (const simulation of safeSimulations) {
      try {
        logger.log(`\n安全测试: ${simulation.name}`);
        await simulation.method();

        // 等待一段时间让测试完成
        await this.sleep(3000);

        this.isRunning = false;
      } catch (error) {
        logger.error(`❌ ${simulation.name} 测试失败:`, error);
        this.isRunning = false;
      }
    }

    logger.log('\n所有安全测试完成！');
  }

  /**
   * 停止当前模拟测试
   */
  stopSimulation() {
    this.isRunning = false;
    logger.log('🛑 模拟测试已停止');
  }
  /**
   * 检查是否正在运行测试
   */
  isTestRunning(): boolean {
    return this.isRunning;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
